// The React Compiler mis-compiles this factory: its inner `use*` hooks close over the factory's
// mutable `listeners` array, and the compiler outlines the `.map`/`.forEach` callbacks to module
// scope, where `listeners` is not defined — a `ReferenceError` at runtime. Opt the whole file out
// of the compiler (memoizing these thin wrappers adds no value anyway). See `"use no memo"`.
'use no memo';
import { useCallback, useEffect, useState } from 'react';
/**
 * Returns a coupled pair of hooks that can be used to emit and subscribe to changes of a specific value
 *
 * @example
 * const {useEmit, useListener} = createSubscribableData<string>();
 *
 * EmitterComponent() {
 *  const [someState, setSomeState] = useState("foo");
 *
 *  useEmit(someState);

 *  ...
 * }
 *
 * ListenerComponent() {
 *
 *  useListener((data: string) => {
 *
 *  })
 *
 *  OR - to immediately react on value changes
 *
 *  const data = useStateListener()
 *
 *  ...
 * }
 *
 *
 */
export const createSubscribableData = () => {
    const listeners = [];
    const addListener = (listener) => {
        listeners.push(listener);
    };
    const removeListener = (listener) => {
        if (listeners.includes(listener)) {
            // eslint-disable-next-line local-rules/no-mutable-array-methods -- pre-existing mutating array call flagged when local-rules/no-mutable-array-methods was added; migrate to the immutable equivalent (toSorted/toReversed/toSpliced) separately
            listeners.splice(listeners.findIndex(sub => sub === listener), 1);
        }
    };
    const useEmit = (data) => {
        const emit = useCallback((data) => {
            listeners.forEach(sub => {
                sub(data);
            });
        }, []);
        useEffect(() => {
            emit(data);
        }, [emit, data]);
    };
    const useListener = (listener) => {
        useEffect(() => {
            if (!listeners.includes(listener)) {
                addListener(listener);
            }
            return () => {
                removeListener(listener);
            };
        }, [listener]);
    };
    const useStateListener = () => {
        const [data, setData] = useState();
        const listener = useCallback((value) => {
            setData(value);
        }, []);
        useListener(listener);
        return data;
    };
    return { useEmit, useListener, useStateListener };
};
